import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material';
import { FocusScope } from '@react-aria/focus';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import './TimeoutScreenConfig';
import { getIconStyle, getRetryButtonSx, getSubtitleSx, getTitleSx, OverlayStyled } from './styles/TimeoutScreenStyles';
import { retryTimeout, timeoutVisibleVar } from '../../../apollo/timeout/timeoutManager';
import AtomButton from '../../../components/atoms/AtomButton/AtomButton';
import AtomTypography from '../../../components/atoms/AtomTypography/AtomTypography';
import CircularArrowIcon from '../../../components/icons/svg/CircularArrowIcon';
import CloudAlertIcon from '../../../components/icons/svg/CloudAlertIcon';

const TITLE_ID = 'timeout-screen-title';
const SUBTITLE_ID = 'timeout-screen-subtitle';

/**
 * Root-mounted overlay shown when a non-exempt GraphQL operation times out.
 * Renders nothing until `timeoutVisibleVar` is true.
 *
 * Retry returns the user to the route they were on when the timeout happened
 * (they cannot normally navigate while the overlay is up, but a redirect can
 * still move them) and remounts the route subtree so its queries re-run.
 *
 * Role-neutral: it covers both the learner and advisor layouts.
 */
const PageTimeoutScreen: React.FC = () => {
  const isVisible = useReactiveVar(timeoutVisibleVar);
  const { t } = useTranslation('TimeoutScreen');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  if (!isVisible) {
    return null;
  }

  const handleRetry = (): void => {
    const timedOutRoute = retryTimeout();

    if (timedOutRoute && timedOutRoute !== `${location.pathname}${location.search}`) {
      navigate(timedOutRoute, { replace: true });
    }
  };

  return (
    <OverlayStyled
      data-testid="timeout-screen"
      data-cy="timeout-screen"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      aria-describedby={SUBTITLE_ID}
    >
      <FocusScope contain restoreFocus autoFocus>
        <CloudAlertIcon data-testid="timeout-screen-icon" style={getIconStyle()} />
        <AtomTypography id={TITLE_ID} fontWeight={500} sx={getTitleSx(theme)}>
          {t('TITLE_TIMEOUT')}
        </AtomTypography>
        <AtomTypography id={SUBTITLE_ID} fontWeight={400} sx={getSubtitleSx(theme)}>
          {t('SUBTITLE_TIMEOUT')}
        </AtomTypography>
        <AtomButton
          variant="contained"
          onClick={handleRetry}
          sx={getRetryButtonSx()}
          data-testid="timeout-screen-retry"
          data-cy="timeout-screen-retry"
          startIcon={<CircularArrowIcon width={20} height={20} color={theme.palette.primary.contrastText} />}
        >
          {t('RETRY_BUTTON_TIMEOUT')}
        </AtomButton>
      </FocusScope>
    </OverlayStyled>
  );
};

export default PageTimeoutScreen;
